import * as Tone from "tone";

type EventHandlerCallback<T> = (target: T) => void;

function sleep(timeToDelay: number) {
  return new Promise((resolve) => setTimeout(() => resolve(true), timeToDelay));
}

export class StreamingAudio {
  private _currentTime: number;

  private _duration: number;

  private _paused: boolean;

  private _ended: boolean;

  private _loop: boolean;

  private _playbackRate: number;

  private _src: string[];

  private _loadedMetadata: boolean;

  private _buffering: boolean;

  private _waiting: boolean;

  private _audioTimer: {
    stamp: ReturnType<typeof requestAnimationFrame> | null;
    delta: number | null;
    triggerDelta: number | null;
  };

  private _audioBuffers: Tone.GrainPlayer[];

  private _currentAudioIndex: number;

  private _currentAudioOffset: number;

  private _eventHandlers: {
    [key: string]: EventHandlerCallback<StreamingAudio>[];
  };

  private _error: Error | null;

  private _audioContext: AudioContext | null;

  constructor(init = true) {
    this._currentTime = 0;
    this._duration = 0;
    this._paused = true;
    this._ended = false;
    this._loop = false;
    this._playbackRate = 1;
    this._src = [];

    this._loadedMetadata = false;
    this._buffering = false;
    this._waiting = false;
    this._audioTimer = {
      stamp: null,
      delta: null,
      triggerDelta: null,
    };

    this._audioBuffers = [];
    this._currentAudioIndex = 0;
    this._currentAudioOffset = 0;

    this._eventHandlers = {};

    this._error = null;

    this._handleSourceNodeEnd = this._handleSourceNodeEnd.bind(this);
    this._handleAudioTimer = this._handleAudioTimer.bind(this);

    this._audioContext = null;

    if (init) {
      this.init();
    }

    return this;
  }

  get audioContext() {
    return this._audioContext;
  }

  get currentTime() {
    return this._currentTime;
  }

  set currentTime(value: number) {
    const normalizedValue = (() => {
      if (value > this._duration) {
        return this._duration;
      }
      if (value < 0) {
        return 0;
      }
      return value;
    })();

    this._triggerEvent("seeking");
    this._pause();
    this._currentTime = normalizedValue;
    this._ended = false;

    const { index: audioBufferIndex, offset: audioBufferOffset } =
      this._audioBuffers.reduce(
        (acc, node, index) => {
          if (acc.sum > normalizedValue) {
            return acc;
          }
          if (!node.loaded) {
            return acc;
          }
          return {
            index,
            offset: normalizedValue - acc.sum,
            sum: acc.sum + node.buffer.duration / this._playbackRate,
          };
        },
        { index: 0, offset: 0, sum: 0 }
      );

    this._currentAudioIndex = audioBufferIndex;
    this._currentAudioOffset = audioBufferOffset;

    if (!this._paused) {
      this._play();
    }

    this._triggerEvent("seeked");
  }

  get duration() {
    if (!this._loadedMetadata) {
      return NaN;
    }
    return this._duration;
  }

  get paused() {
    return this._paused;
  }

  get ended() {
    return this._ended;
  }

  get loop() {
    return this._loop;
  }

  set loop(value: boolean) {
    this._loop = !!value;
  }

  get playbackRate() {
    return this._playbackRate;
  }

  set playbackRate(value: number) {
    if (this._playbackRate !== value) {
      this._pause();

      const currentProgress = this._currentTime / this._duration;

      this._playbackRate = value;
      this._duration = this._audioBuffers.reduce((acc, node) => {
        if (!node.loaded) {
          return acc;
        }
        return acc + node.buffer.duration / this._playbackRate;
      }, 0);
      this.currentTime = this._duration * currentProgress;
      this._triggerEvent("durationchange");
    }
  }

  get src() {
    return this._src;
  }

  set src(value: string | string[]) {
    // if (!this._buffering) {
    //   console.error("StreamingAudioError: Cannot change the url after buffering is finished.");
    //   return;
    // }

    const normalizedValue = (() => {
      if (Array.isArray(value)) {
        return [...value];
      }
      return [value];
    })();

    if (this._src.some((src) => !normalizedValue.includes(src))) {
      this._pause();
      this.clear();
      // this._currentTime = 0;
      // this._duration = 0;
      // this._currentAudioIndex = 0;
      // this._currentAudioOffset = 0;
      // this._error = null;
    }

    this._audioBuffers = normalizedValue.map((v) => {
      const storedIndex = this._src.findIndex((src) => src === v);

      if (storedIndex !== -1) {
        return this._audioBuffers[storedIndex];
      }

      return new Tone.GrainPlayer({
        url: v,
        loop: false,
        grainSize: 0.1,
        overlap: 0.05,
        onload: () => {
          this._duration = this._audioBuffers.reduce((acc, node) => {
            if (!node.loaded) {
              return acc;
            }
            return acc + node.buffer.duration / this._playbackRate;
          }, 0);

          if (!this._loadedMetadata) {
            this._loadedMetadata = true;
            this._triggerEvent("loadedmetadata");
            this._triggerEvent("canplay");
          } else {
            this._triggerEvent("durationchange");
            if (!this._buffering) {
              this._triggerEvent("canplaythrough");
            }
          }
        },
      });
    });

    this._src = normalizedValue;
  }

  get buffering() {
    return this._buffering;
  }

  get error(): Error | undefined {
    if (!this._error) {
      return;
    }
    return this._error;
  }

  set error(value: Error) {
    this._error = value;
    this._triggerEvent("error");

    this.buffer.end();
    this._pause();
  }

  readonly buffer = {
    start: () => {
      this._triggerEvent("bufferstart");
      this._buffering = true;
    },
    end: () => {
      this._triggerEvent("bufferend");
      this._buffering = false;
    },
  };

  private _playSourceNode() {
    if (this._audioTimer.stamp) {
      cancelAnimationFrame(this._audioTimer.stamp);
    }
    this._audioTimer = {
      stamp: null,
      delta: null,
      triggerDelta: null,
    };

    const buffer = this._audioBuffers[this._currentAudioIndex];

    if (this._paused || this._error) {
      return;
    }

    if (!buffer || !buffer?.loaded) {
      if (this._buffering || !buffer?.loaded) {
        if (!this._waiting) {
          this._triggerEvent("waiting");
        }
        this._waiting = true;
        this._handleSourceNodeWait().then((result) => {
          if (this._paused || this._error) {
            return;
          }

          if (result) {
            this._playSourceNode();
          } else {
            this._handleAudioEnd();
          }
        });
        return;
      } else {
        this._handleAudioEnd();
        return;
      }
    }

    if (this._waiting) {
      this._triggerEvent("playing");
    }
    this._waiting = false;
    this._audioTimer.stamp = requestAnimationFrame(this._handleAudioTimer);

    buffer.playbackRate = this._playbackRate;
    // buffer.onstop = () => {
    //   this._handleSourceNodeEnd();
    // };
    buffer.toDestination();
    try {
      buffer.start(0, this._currentAudioOffset);
    } catch {}
  }

  private _handleAudioEnd() {
    this._currentAudioIndex = 0;
    this._currentAudioOffset = 0;

    if (this._loop) {
      this._currentTime = 0;
      this._currentAudioIndex = 0;
      this._currentAudioOffset = 0;
      this._play();
    } else {
      this._ended = true;
      this._paused = true;
      this._pause();
      this._triggerEvent("ended");
    }
  }

  private async _handleSourceNodeWait(): Promise<boolean> {
    this._pause();

    const buffer = this._audioBuffers[this._currentAudioIndex];

    if (!this._buffering && !buffer) {
      return false;
    }

    if (buffer?.loaded) {
      return true;
    }

    await sleep(500);

    return this._handleSourceNodeWait();
  }

  private _handleSourceNodeEnd() {
    if (this._audioTimer.stamp) {
      cancelAnimationFrame(this._audioTimer.stamp);
    }
    this._audioTimer = {
      stamp: null,
      delta: null,
      triggerDelta: null,
    };
    this._audioBuffers.forEach((audioBuffer) => {
      audioBuffer.disconnect();
    });

    if (!this._paused) {
      this._currentAudioIndex++;
      this._currentAudioOffset = 0;
      this._playSourceNode();
    }
  }

  private _handleAudioTimer(time: number) {
    if (!this._audioTimer.delta) {
      this._audioTimer.delta = time;
    }
    if (!this._audioTimer.triggerDelta) {
      this._audioTimer.triggerDelta = time;
    }

    // this._currentTime = this._currentTime + (time - this._audioTimer.delta) / 1000;
    this._currentAudioOffset =
      this._currentAudioOffset + (time - this._audioTimer.delta) / 1000;
    this._currentTime = this._audioBuffers.reduce((acc, audioBuffer, index) => {
      if (index < this._currentAudioIndex && audioBuffer.buffer.loaded) {
        return acc + audioBuffer.buffer.duration / this._playbackRate;
      }
      if (index === this._currentAudioIndex) {
        return acc + this._currentAudioOffset;
      }
      return acc;
    }, 0);
    this._audioTimer.delta = time;

    if (
      this._currentAudioOffset >=
      this._audioBuffers[this._currentAudioIndex].buffer.duration /
        this._playbackRate
    ) {
      return this._handleSourceNodeEnd();
    }

    if (time - this._audioTimer.triggerDelta > 250) {
      this._triggerEvent("timeupdate");
      this._audioTimer.triggerDelta = time;
    }

    this._audioTimer.stamp = requestAnimationFrame(this._handleAudioTimer);
  }

  private _play() {
    if (this._ended) {
      this._ended = false;
      this._currentTime = 0;
      this._currentAudioIndex = 0;
      this._currentAudioOffset = 0;
    }
    this._playSourceNode();
  }

  play() {
    if (this._audioContext) {
      this._audioContext.resume();
      this._triggerEvent("play");
      this._paused = false;
      this._play();
    }
  }

  private _pause() {
    this._audioBuffers.forEach((audioBuffer) => {
      audioBuffer.stop();
      audioBuffer.disconnect();
    });

    if (this._audioTimer.stamp) {
      cancelAnimationFrame(this._audioTimer.stamp);
    }
    this._audioTimer = {
      stamp: null,
      delta: null,
      triggerDelta: null,
    };
  }

  pause() {
    this._pause();
    this._paused = true;
    this._waiting = false;
    this._triggerEvent("pause");
  }

  init() {
    this._audioContext = new AudioContext();
    Tone.setContext(this._audioContext);
  }

  clear() {
    this._currentTime = 0;
    this._duration = 0;
    this._ended = false;
    this._src = [];

    this._loadedMetadata = false;
    this._buffering = false;
    this._waiting = false;

    if (this._audioTimer.stamp) {
      cancelAnimationFrame(this._audioTimer.stamp);
    }

    this._audioTimer = {
      stamp: null,
      delta: null,
      triggerDelta: null,
    };

    this._audioBuffers.forEach((audioBuffer) => {
      audioBuffer.disconnect();
      audioBuffer.dispose();
    });

    this._audioBuffers = [];
    this._currentAudioIndex = 0;
    this._currentAudioOffset = 0;

    this._error = null;
  }

  remove() {
    this._audioContext?.close();
    this.clear();

    this._paused = true;
    this._playbackRate = 1;
    this._loop = false;
    this._eventHandlers = {};
    this._audioContext = null;
  }

  private _triggerEvent(eventName: string) {
    const eventHandlers = this._eventHandlers[eventName];

    if (eventHandlers) {
      eventHandlers.forEach((handler) => handler(this));
    }
  }

  addEventListener(
    eventName: string,
    callback: EventHandlerCallback<StreamingAudio>
  ) {
    if (!this._eventHandlers[eventName]) {
      this._eventHandlers[eventName] = [];
    }

    const eventHandlers = this._eventHandlers[eventName];

    if (eventHandlers.some((handler) => handler === callback)) {
      return;
    }
    eventHandlers.push(callback);
  }

  removeEventListener(
    eventName: string,
    callback: EventHandlerCallback<StreamingAudio>
  ) {
    const eventHandlers = this._eventHandlers[eventName];

    if (!eventHandlers) {
      return;
    }
    this._eventHandlers[eventName] = eventHandlers.filter(
      (handler) => handler !== callback
    );
  }
}
